/*
 * © 2024 Snyk Limited
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConnectionTimeoutError = exports.ConfigEnvironmentConsistencyIssueError = exports.ConfigEnvironmentFailedError = void 0;
const types_1 = require("../types");
const problem_error_1 = require("../problem-error");
/**
 * @class
 * @name ConfigEnvironmentFailedError
 * @description The specified environment cannot be used. As a result, the configuration remains unchanged.Provide the correct specifications for the environment and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/config-environment](https://docs.snyk.io/snyk-cli/commands/config-environment)
 * @summary Unable to set environment
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ConfigEnvironmentFailedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to set environment',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0001',
            status: 200,
            description: `The specified environment cannot be used. As a result, the configuration remains unchanged.Provide the correct specifications for the environment and try again.`,
            errorCode: 'SNYK-CLI-0001',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/config-environment',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ConfigEnvironmentFailedError = ConfigEnvironmentFailedError;
/**
 * @class
 * @name ConfigEnvironmentConsistencyIssueError
 * @description You can configure the CLI in different ways, for example via Environment Variables or configuration file.
 * If one parameter is configured multiple times, it is probably unintentional and might cause unexpected behaviour.
 * Review configured environment variables and ensure that everything is intentional. If so, you can skip this check by using --no-check.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/config-environment](https://docs.snyk.io/snyk-cli/commands/config-environment)
 * @summary Possible inconsistent configuration
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ConfigEnvironmentConsistencyIssueError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Possible inconsistent configuration',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0002',
            status: 200,
            description: `You can configure the CLI in different ways, for example via Environment Variables or configuration file.
If one parameter is configured multiple times, it is probably unintentional and might cause unexpected behaviour.
Review configured environment variables and ensure that everything is intentional. If so, you can skip this check by using --no-check.`,
            errorCode: 'SNYK-CLI-0002',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/config-environment',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ConfigEnvironmentConsistencyIssueError = ConfigEnvironmentConsistencyIssueError;
/**
 * @class
 * @name ConnectionTimeoutError
 * @description A request to the Snyk API has unexpectedly timeout. Check Snyk status, then try again.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * @summary Request to Snyk API timeout
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ConnectionTimeoutError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Request to Snyk API timeout',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-os-7001',
            status: 504,
            description: `A request to the Snyk API has unexpectedly timeout. Check Snyk status, then try again.`,
            errorCode: 'SNYK-OS-7001',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ConnectionTimeoutError = ConnectionTimeoutError;
//# sourceMappingURL=CLI-error-catalog.js.map